import { restartableTask } from 'ember-concurrency';
import moment from 'moment-timezone';
import { POResourceName } from 'volta/models/planning-order';
import { SkuResourceName } from 'volta/models/sku';
import {
  LabelsForAssignTo,
  SKU_TASK_TYPES,
  SkuTaskResourceName,
  TaskEvents,
  TaskResourceName
} from 'volta/models/task';
import TaskService from 'volta/services/task-service';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { inFilter } from 'volta/utils/filters-utils';

import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type IntlService from 'ember-intl/services/intl';
import type EventStreamService from 'volta/services/event-stream';
import type StoreService from 'volta/services/store';
import type { IWidgetArgs } from '../';
import type { IWidgetDueTaskArgs, TAssignTo, TTaskType, UTask } from 'volta/models/types/tasks';
import type AuthorizationService from 'volta/services/authorization';
import { isPresent } from '@ember/utils';

export default class DueTasksWidget extends Component<IWidgetArgs<IWidgetDueTaskArgs>> {
  @service taskService!: TaskService;
  @service eventStream!: EventStreamService;
  @service intl!: IntlService;
  @service store!: StoreService;
  @service authorization!: AuthorizationService;

  @tracked tasks: UTask[] = [];
  @tracked filterTypes: TTaskType[] = SKU_TASK_TYPES;

  resourceName = SkuTaskResourceName;
  skuResourceName = SkuResourceName;
  filterOptions: TTaskType[] = SKU_TASK_TYPES;

  get authorizedEntityTypes() {
    return this.authorization.canViewAnyList(SkuResourceName.plural, POResourceName.plural);
  }

  get widgetArgs(): IWidgetDueTaskArgs | undefined {
    return { ...{ types: SKU_TASK_TYPES }, ...this.args.widget.args };
  }

  get headerTitle() {
    return this.intl.t(
      this.widgetArgs?.assignTo
        ? LabelsForAssignTo[this.widgetArgs.assignTo as TAssignTo].title
        : 'dashboardPage.tasks.title'
    );
  }

  get emptyLabel() {
    return this.intl.t(
      this.widgetArgs?.assignTo
        ? LabelsForAssignTo[this.widgetArgs.assignTo as TAssignTo].empty
        : 'tasksPage.noTasks'
    );
  }

  get overdueTasks() {
    return this.tasks.filter((t) => moment(t.dueDate).isBefore(moment()));
  }

  get currentTasks() {
    return this.tasks.filter((t) => moment(t.dueDate).isSameOrAfter(moment()));
  }

  constructor(owner: any, args: IWidgetArgs<IWidgetDueTaskArgs>) {
    super(owner, args);
    this.eventStream.addHandler(
      this.skuResourceName.plural,
      TaskEvents.TaskResolved,
      this.handleTaskResolve,
      this
    );
  }

  fetchTasks = restartableTask(async () => {
    const { types = [], assignTo } = this.widgetArgs ?? {};

    const query = {
      filter: TaskService.queryFiltersToApiFilters(this.taskService.assignQueryFilter(assignTo))
    };
    query.filter.taskType = inFilter(isPresent(types) ? types : SKU_TASK_TYPES);

    this.tasks = (await this.taskService.fetchTasks.perform(query)).toArray();
  });

  async handleTaskResolve(payload: any) {
    const task = normalize(TaskResourceName.singular, payload) as UTask;
    this.tasks = this.tasks.filter((t) => t.id !== task.id);
  }
}
